export const HIRING_CATEGORIES = [
    { key: 'IN_PROGRESS', label: 'In Progress', statuses: ['NEW', 'PRE_HIRE_SENT', 'PRE_HIRE_COMPLETED'] },
    { key: 'HIRED', label: 'Hired', statuses: ['HIRED'] },
    { key: 'REJECTED', label: 'Rejected', statuses: ['REJECTED'] },
];

export function rowsInCategory(rows, categoryKey) {
    const category = HIRING_CATEGORIES.find((item) => item.key === categoryKey);
    const statuses = category ? category.statuses : [];
    return (rows || []).filter((row) => statuses.includes(row.status));
}

const TYPE_LABELS = { Company: 'Company', Lease: 'Lease', Owner: 'Owner' };
const STATUS_LABELS = {
    NEW: 'New',
    PRE_HIRE_SENT: 'Pre-Hire Sent',
    PRE_HIRE_COMPLETED: 'Pre-Hire Completed',
    HIRED: 'Hired',
    REJECTED: 'Rejected',
};
const INSURANCE_LABELS = {
    NOT_SENT: 'Not Sent',
    SENT: 'Sent',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
};
const PAYMENT_LABELS = { PerMile: 'Per Mile', Percentage: 'Percentage', Salary: 'Salary' };
const STATUS_ORDER = ['NEW', 'PRE_HIRE_SENT', 'PRE_HIRE_COMPLETED', 'HIRED', 'REJECTED'];

export const HIRING_COLUMNS = [
    { key: 'name', label: 'Name', size: '1.25fr' },
    { key: 'contact', label: 'Contact', size: '1.45fr', sortable: false },
    { key: 'driverType', label: 'Type', size: '0.65fr' },
    { key: 'payment', label: 'Payment', size: '0.9fr' },
    { key: 'experience', label: 'Experience', size: '0.75fr' },
    { key: 'note', label: 'Note', size: '1.6fr', sortable: false },
    { key: 'insuranceStatus', label: 'Insurance Status', size: '1.1fr' },
    { key: 'status', label: 'Status', size: '1.15fr' },
    { key: 'comingDate', label: 'Coming Date', size: '0.85fr' },
    { key: 'companyName', label: 'Target Company', size: '1.25fr', company: true },
    { key: 'files', label: 'Files', size: '0.5fr', sortable: false },
    { key: 'actions', label: 'Actions', size: '0.7fr', sortable: false },
    { key: 'reference', label: 'Reference', size: '1fr', sortable: false },
];

export function hiringColumns(showCompany) {
    return HIRING_COLUMNS.filter((column) => !column.company || showCompany);
}

export function columnTemplate(showCompany) {
    const sizes = hiringColumns(showCompany).map((column) => `minmax(0, ${column.size})`);
    return `2rem ${sizes.join(' ')} 2.5rem`;
}

export function statusLabel(value) {
    return STATUS_LABELS[value] || value || '-';
}

export function insuranceLabel(value) {
    return INSURANCE_LABELS[value] || value || '-';
}

export function contactLabel(row) {
    const parts = [row?.phoneNumber, row?.email]
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter(Boolean);
    return parts.length ? parts.join(' / ') : '-';
}

export function typeLabel(value) {
    return TYPE_LABELS[value] || value || '-';
}

export function firstName(fullName) {
    const text = fullName == null ? '' : String(fullName).trim();
    if (!text) return '';
    return text.split(/\s+/)[0];
}

export function displayName(row, wrapped) {
    const name = row?.name && String(row.name).trim() ? String(row.name).trim() : '-';
    if (wrapped || !row?.partnerName) return name;
    const teammate = firstName(row.partnerName);
    return teammate ? `${name} (${teammate})` : name;
}

export function experienceLabel(row) {
    if (row?.experienceYears == null && row?.experienceMonths == null) return '-';
    return `${row.experienceYears || 0}y ${row.experienceMonths || 0}m`;
}

export function paymentLabel(row) {
    const type = PAYMENT_LABELS[row?.paymentType];
    const amount = row?.paymentAmount;
    const hasAmount = amount != null && amount !== '';
    if (!type && !hasAmount) return '-';
    if (!hasAmount) return type;
    const number = Number(amount);
    const shown = Number.isFinite(number) ? number.toLocaleString(undefined, { maximumFractionDigits: 4 }) : String(amount);
    const amountText = row?.paymentType === 'Percentage' ? `${shown}%` : `$${shown}`;
    return type ? `${type} (${amountText})` : amountText;
}

export function fileCount(row) {
    return Array.isArray(row?.files) ? row.files.length : 0;
}

function blank(value) {
    return value == null || String(value).trim() === '';
}

function compareValues(left, right, desc) {
    const leftEmpty = blank(left);
    const rightEmpty = blank(right);
    if (leftEmpty && rightEmpty) return 0;
    if (leftEmpty) return 1;
    if (rightEmpty) return -1;
    const leftNumber = typeof left === 'number' ? left : Number.NaN;
    const rightNumber = typeof right === 'number' ? right : Number.NaN;
    let result;
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        result = leftNumber - rightNumber;
    } else {
        result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
    }
    return desc ? -result : result;
}

function statusRank(value) {
    const index = STATUS_ORDER.indexOf(value);
    return index === -1 ? STATUS_ORDER.length : index;
}

function experienceMonths(row) {
    if (row?.experienceYears == null && row?.experienceMonths == null) return null;
    return (Number(row.experienceYears) || 0) * 12 + (Number(row.experienceMonths) || 0);
}

function sortValue(row, key) {
    switch (key) {
        case 'driverType': return typeLabel(row.driverType);
        case 'payment': return paymentLabel(row);
        case 'name': return row.name;
        case 'contact': return contactLabel(row);
        case 'reference': return row.reference;
        case 'experience': return experienceMonths(row);
        case 'note': return row.note;
        case 'insuranceStatus': return insuranceLabel(row.insuranceStatus);
        case 'status': return statusRank(row.status);
        case 'comingDate': return row.comingDate;
        case 'companyName': return row.companyName;
        case 'files': return fileCount(row);
        case 'actions': return row.latestAction;
        default: return null;
    }
}

function adjacentPartner(ordered, index) {
    const row = ordered[index];
    const next = ordered[index + 1];
    if (!row?.teamId || !next || next.teamId !== row.teamId) return null;
    return next;
}

export function orderHiringRows(rows, sort) {
    const source = Array.isArray(rows) ? rows : [];
    const ordered = sort
        ? [...source].sort((left, right) => compareValues(sortValue(left, sort.key), sortValue(right, sort.key), sort.desc))
        : [...source];
    const blocks = [];
    for (let index = 0; index < ordered.length; index += 1) {
        const row = ordered[index];
        const partner = adjacentPartner(ordered, index);
        if (partner) {
            blocks.push({ kind: 'pair', rows: [row, partner] });
            index += 1;
        } else {
            blocks.push({ kind: 'single', rows: [row] });
        }
    }
    return blocks;
}
